import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import { Alert, Button, TextField } from 'ui/baseComponents'
import { useTagList } from '../../graphql/tagQueries'
import { highlightsApi } from '../../api/highlightsApi'

const PushNotificationsTab = () => {
  const { data: tagsData } = useTagList()
  const tags = tagsData?.tags || []

  const [tagId, setTagId] = useState('')
  const [patientIds, setPatientIds] = useState('')
  const [body, setBody] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)

  const handleSend = useCallback(async () => {
    if (!tagId) {
      setError('Select a tag')
      return
    }
    if (!body.trim()) {
      setError('Enter a message')
      return
    }

    setError(null)
    setResult(null)
    setSending(true)

    try {
      const payload = {
        tag_id: tagId,
        alert_body: body.trim(),
      }

      const ids = patientIds
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
      if (ids.length > 0) {
        payload.patient_ids = ids
      }

      const response = await highlightsApi.sendPushNotification(payload)
      setResult(
        `Push notification sent successfully. ${JSON.stringify(response)}`,
      )
      setBody('')
      setPatientIds('')
    } catch (err) {
      setError('Failed to send push notification. Please try again.')
    } finally {
      setSending(false)
    }
  }, [tagId, body, patientIds])

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Send Push Notification
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {result}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl size="small" required>
            <InputLabel>Tag</InputLabel>
            <Select
              value={tagId}
              label="Tag"
              onChange={(e) => setTagId(e.target.value)}
            >
              {tags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {tag.tag} ({tag.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Patient IDs (optional, one per line)"
            value={patientIds}
            onChange={(e) => setPatientIds(e.target.value)}
            multiline
            rows={4}
            size="small"
            placeholder="Leave empty to send to all patients with the tag"
          />

          <TextField
            label="Notification Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            rows={3}
            size="small"
            required
            placeholder="Push notification message body"
          />

          <Button
            type="primary"
            onClick={handleSend}
            disabled={sending}
            sx={{ alignSelf: 'flex-start' }}
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Push notifications will not be sent to patients who have opted out
            of custom highlights cards.
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default PushNotificationsTab
